import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import BookSlotModal from './BookSlotModal';
import ConfirmDialog from './ConfirmDialog';
import usePageTitle from '../hooks/usePageTitle';
import MemberPicker from './MemberPicker';
import Larry from './Larry';
import {
  getMeeting,
  getCurrentMember,
  bookSlot,
  cancelSlotBooking,
  assignSlot,
  unassignSlot,
  addMeetingSlot,
  deleteMeetingSlot,
  getMeetingAttendance,
  generateCheckinToken,
  markAttendanceManual,
  getMembers,
  createSpeech,
  deleteSpeech,
  getSpeech,
} from '../services/api';
import useBackgroundTranscription, { transcriptionTimeoutMs } from '../hooks/useBackgroundTranscription';
import { probeDuration } from '../utils/media';

// Per-slot banner: polls one speech in the background and reports completion upward.
// Mounted once per slot whose speech has audio_file_id but no metrics yet.
// N independent instances run concurrently — each owns its own hook state.
function SlotTranscriptionBanner({ speechId, durationSeconds = 0, onComplete }) {
  const { elapsedSeconds, errorMsg, timedOut } = useBackgroundTranscription({
    enabled: !!speechId,
    intervalMs: 15000,
    // Known duration (set at upload time) scales the timeout; on refresh it's
    // unknown and the 10-minute default applies.
    timeoutMs: transcriptionTimeoutMs(durationSeconds),
    pollFn: () => getSpeech(speechId),
    isComplete: (res) => !!res?.speech?.metrics,
    onComplete: (res) => onComplete?.(res.speech),
  });
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  const timeStr = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

  // Terminal state — polling has stopped; don't keep an infinite spinner.
  if (timedOut || errorMsg) {
    return (
      <div
        className="px-3 py-2 rounded-lg flex items-center gap-3"
        style={{ background: 'var(--color-warning-dim)', border: '1px solid var(--color-warning-border)' }}
      >
        <span style={{ fontSize: '0.9rem' }}>⚠️</span>
        <p className="text-xs font-bold" style={{ color: 'var(--color-warning)' }}>
          Transcription is taking longer than expected.{' '}
          <span className="font-medium" style={{ color: 'var(--text-faint)' }}>
            It may have failed — an admin can clear the analysis and re-upload the audio.
          </span>
        </p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes slot-transcription-indeterminate {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
      <div
        className="px-3 py-2 rounded-lg flex items-center gap-3"
        style={{ background: 'var(--color-info-dim)', border: '1px solid var(--color-info-border)' }}
      >
        <span style={{ fontSize: '0.9rem' }}>⏳</span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold" style={{ color: 'var(--color-info)' }}>
            Transcribing… <span className="font-medium" style={{ color: 'var(--text-faint)' }}>{timeStr} elapsed</span>
          </p>
          <div
            className="w-full rounded-full overflow-hidden relative mt-1"
            style={{ height: '3px', background: 'var(--accent-dim)' }}
          >
            <div
              style={{
                position: 'absolute', top: 0, left: 0,
                height: '100%', width: '25%', borderRadius: '9999px',
                background: 'linear-gradient(90deg, var(--accent), var(--color-info))',
                animation: 'slot-transcription-indeterminate 1.6s ease-in-out infinite',
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}

// Theme-aware input helpers
const focusAmber = (e) => { e.currentTarget.style.borderColor = 'var(--border-accent)'; };
const blurAmber  = (e) => { e.currentTarget.style.borderColor = 'var(--border-accent)'; };
const inputStyle = {
  background: 'var(--bg-input)',
  border: '1px solid var(--border-accent)',
  color: 'var(--text-primary)',
  outline: 'none',
  borderRadius: '0.5rem',
  padding: '0.5rem 0.75rem',
  width: '100%',
  fontSize: '0.875rem',
  boxSizing: 'border-box',
};

/**
 * MeetingDetail
 * View a meeting's details: date, status, slots, and associated speeches.
 * Admin flow: assign speakers pre-meeting, add YouTube URL post-meeting.
 * Member flow: book/cancel slots, see assigned speakers + synopsis.
 */
export default function MeetingDetail() {
  const { meetingId } = useParams();
  const [meeting, setMeeting] = useState(null);
  usePageTitle(meeting ? `Meeting ${meeting.meeting_number ?? meetingId}` : 'Meeting');
  const [speeches, setSpeeches] = useState([]);
  const [member, setMember] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const prevMeetingIdRef = useRef(null);

  // QR modal state
  const [qrModal, setQrModal] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  // Manual attendance state
  const [showMarkPresent, setShowMarkPresent] = useState(false);
  const [markPresentSearch, setMarkPresentSearch] = useState('');

  // Slot booking modal state (non-admin)
  const [bookingSlot, setBookingSlot] = useState(null);

  // Inline assign form state (admin pre-meeting)
  const [assignSlotId, setAssignSlotId] = useState(null);
  const [assignForm, setAssignForm] = useState({ speaker_id: '', presentation_title: '', synopsis: '' });
  const [assigning, setAssigning] = useState(false);
  const [assignMessage, setAssignMessage] = useState(null);

  // Inline YouTube upload form state (admin post-meeting)
  const [uploadSlotId, setUploadSlotId] = useState(null);
  const [uploadForm, setUploadForm] = useState({ speakerId: '', title: '', youtubeUrl: '', audioFile: null, tagsInput: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadMessage, setUploadMessage] = useState(null);
  // Per-slot audio durations probed at upload time — lets the transcription
  // banner scale its timeout. Lost on refresh (banner falls back to default).
  const [uploadDurations, setUploadDurations] = useState({});

  // Remove speech confirmation dialog state
  // { slotId, speechId, hasSpeech } — null when dialog is closed
  const [confirmRemove, setConfirmRemove] = useState(null);

  useEffect(() => {
    if (prevMeetingIdRef.current === meetingId) return;
    prevMeetingIdRef.current = meetingId;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [meetingRes, memberRes, attendanceRes] = await Promise.all([
        getMeeting(meetingId),
        getCurrentMember().catch(() => null),
        getMeetingAttendance(meetingId).catch(() => null),
      ]);

      if (!meetingRes?.meeting) throw new Error('Meeting not found');

      setMeeting(meetingRes.meeting);
      setSpeeches(meetingRes.speeches || []);
      if (memberRes?.member) setMember(memberRes.member);
      if (attendanceRes?.attendance) setAttendance(attendanceRes.attendance);
    } catch (err) {
      console.error('[MeetingDetail] Load error:', err);
      setError(err.message || 'Failed to load meeting');
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async () => {
    if (allMembers.length > 0) return;
    try {
      const res = await getMembers({ status: 'approved' });
      if (res?.members) setAllMembers(res.members.filter(m => m.is_active !== false && m.is_active !== 'false'));
    } catch {
      // Silently fail — admin will see empty dropdown
    }
  };

  const handleBookSlot = (slot) => {
    setActionError(null);
    setBookingSlot(slot);
  };

  const handleBookingConfirm = async (presentation_title, synopsis) => {
    await bookSlot(bookingSlot.slot_id, { presentation_title, synopsis });
    setBookingSlot(null);
    loadData();
  };

  const handleCancelSlot = async (slotId) => {
    setActionError(null);
    try {
      await cancelSlotBooking(slotId);
      loadData();
    } catch (err) {
      setActionError(err.message || 'Failed to cancel booking');
    }
  };

  const openAssignForm = async (slot) => {
    setUploadSlotId(null);
    setAssignSlotId(slot.slot_id);
    setAssignForm({
      speaker_id: slot.speaker_id || '',
      presentation_title: slot.presentation_title || '',
      synopsis: slot.synopsis || '',
    });
    setAssignMessage(null);
    await loadMembers();
  };

  const handleAssignSlot = async (slot) => {
    if (!assignForm.speaker_id) {
      setAssignMessage({ type: 'error', text: 'Please select a speaker.' });
      return;
    }
    setAssigning(true);
    setAssignMessage(null);
    try {
      await assignSlot(slot.slot_id, {
        speaker_id: assignForm.speaker_id,
        presentation_title: assignForm.presentation_title.trim() || null,
        synopsis: assignForm.synopsis.trim() || null,
      });
      setAssignSlotId(null);
      loadData();
    } catch (err) {
      setAssignMessage({ type: 'error', text: err.message || 'Assignment failed.' });
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignSlot = async (slotId, force = false) => {
    setActionError(null);
    try {
      await unassignSlot(slotId, force);
      loadData();
    } catch (err) {
      setActionError(err.message || 'Failed to unassign slot');
    }
  };

  const openUploadForm = async (slot) => {
    setAssignSlotId(null);
    setUploadSlotId(slot.slot_id);
    setUploadForm({
      speakerId: slot.speaker_id || '',
      title: slot.presentation_title || '',
      youtubeUrl: '',
      audioFile: null,
      tagsInput: '',
    });
    setUploadMessage(null);
    if (!slot.speaker_id) await loadMembers();
  };

  const handleUploadSpeech = async (slot) => {
    const speakerId = slot.speaker_id || uploadForm.speakerId;
    if (!speakerId) {
      setUploadMessage({ type: 'error', text: 'Please select a speaker.' });
      return;
    }
    if (!uploadForm.title.trim()) {
      setUploadMessage({ type: 'error', text: 'Speech title is required.' });
      return;
    }
    setUploading(true);
    setUploadPercent(0);
    setUploadMessage(null);
    try {
      const formData = new FormData();
      formData.append('slot_id', slot.slot_id);
      formData.append('speaker_id', speakerId);
      formData.append('meeting_id', meeting.meeting_id);
      formData.append('title', uploadForm.title.trim());
      if (slot.synopsis) formData.append('synopsis', slot.synopsis);
      if (uploadForm.youtubeUrl) formData.append('youtube_url', uploadForm.youtubeUrl.trim());
      if (uploadForm.audioFile) formData.append('audio', uploadForm.audioFile);
      const tags = uploadForm.tagsInput.split(',').map(t => t.trim()).filter(Boolean).slice(0, 10);
      if (tags.length > 0) formData.append('tags', JSON.stringify(tags));
      if (uploadForm.audioFile) {
        const duration = await probeDuration(uploadForm.audioFile);
        if (duration > 0) {
          formData.append('duration', String(duration));
          // Remember per-slot so the transcription banner can scale its timeout.
          setUploadDurations(prev => ({ ...prev, [slot.slot_id]: duration }));
        }
      }
      const res = await createSpeech(formData, { onProgress: (pct) => setUploadPercent(pct) });
      setUploadSlotId(null);
      // If the speech is still transcribing, splice it into the list directly so the
      // banner mounts immediately. A full loadData() would also work but interrupts the
      // banner mid-animation and is wasteful when we already have the new speech.
      if (res?.speech) {
        setSpeeches(prev => {
          const without = prev.filter(sp => sp.slot_id !== slot.slot_id);
          return [...without, res.speech];
        });
        // Mark slot as completed locally so the UI reflects the new state.
        setMeeting(prev => prev ? {
          ...prev,
          slots: prev.slots.map(s => s.slot_id === slot.slot_id ? { ...s, status: 'completed', speaker_id: speakerId } : s),
        } : prev);
      } else {
        loadData();
      }
    } catch (err) {
      setUploadMessage({ type: 'error', text: err.message || 'Upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  const handleAddSlot = async () => {
    setActionError(null);
    try {
      await addMeetingSlot(meetingId);
      loadData();
    } catch (err) {
      setActionError(err.message || 'Failed to add slot');
    }
  };

  const handleDeleteSlot = async (slotId) => {
    setActionError(null);
    try {
      await deleteMeetingSlot(slotId);
      loadData();
    } catch (err) {
      setActionError(err.message || 'Failed to delete slot');
    }
  };

  const handleRemoveClick = (slotId, speechId) => {
    setConfirmRemove({ slotId, speechId });
  };

  const handleConfirmRemove = async () => {
    const { slotId, speechId } = confirmRemove;
    setConfirmRemove(null);
    setActionError(null);
    try {
      if (speechId) {
        // Deletes feedback + speech + resets slot to booked/open
        await deleteSpeech(speechId);
      } else {
        await unassignSlot(slotId, true);
      }
      loadData();
    } catch (err) {
      setActionError(err.message || 'Failed to remove speech');
    }
  };

  const handleGenerateQR = async () => {
    setQrLoading(true);
    setActionError(null);
    try {
      const res = await generateCheckinToken(meetingId);
      setQrModal({ qrDataUrl: res.qrDataUrl, checkinUrl: res.checkinUrl, expiresAt: res.expiresAt });
    } catch (err) {
      setActionError(err.message || 'Failed to generate QR code');
    } finally {
      setQrLoading(false);
    }
  };

  const handleShowMarkPresent = async () => {
    await loadMembers();
    setMarkPresentSearch('');
    setShowMarkPresent(true);
  };

  const handleMarkPresent = async (memberId) => {
    setActionError(null);
    try {
      await markAttendanceManual(meetingId, memberId);
      const res = await getMeetingAttendance(meetingId);
      if (res?.attendance) setAttendance(res.attendance);
    } catch (err) {
      setActionError(err.message || 'Failed to mark present');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-7 rounded-xl w-48" style={{ background: 'var(--accent-dim)' }} />
        <div className="h-4 rounded-lg w-32" style={{ background: 'var(--bg-input)' }} />
        <div className="space-y-3 mt-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="rounded-2xl p-5 flex items-center gap-4" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
              <div className="w-10 h-10 rounded-full flex-shrink-0" style={{ background: 'var(--accent-dim)' }} />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 rounded-lg w-40" style={{ background: 'var(--bg-input)' }} />
                <div className="h-3 rounded-lg w-24" style={{ background: 'var(--bg-input)' }} />
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-center pt-4">
          <Larry mood="thinking" size={48} message="Loading meeting…" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-2xl mb-3" style={{ color: 'var(--text-faint)' }}>⚠</p>
        <p className="font-medium text-sm" style={{ color: 'var(--text-muted)' }}>{error}</p>
        <Link
          to="/calendar"
          className="inline-block mt-4 text-sm font-medium transition-colors"
          style={{ color: 'var(--accent)' }}
        >
          ← Back to Schedule
        </Link>
      </div>
    );
  }

  if (!meeting) return null;

  const { title, meeting_date, status, slots = [] } = meeting;
  const isPast = new Date(meeting_date) < new Date().setHours(0, 0, 0, 0);
  const isTodayOrPast = new Date(meeting_date) <= new Date();
  const isAdmin = member?.role === 'admin';
  const canCancel = member?.role !== 'member';
  const memberSlot = slots.find(s => s.speaker_id === member?.member_id);
  const bookedCount = slots.filter(s => s.status === 'booked' || s.status === 'completed').length;

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });

  const statusStyle = {
    scheduled: { background: 'var(--color-warning-dim)', border: '1px solid var(--color-warning-border)', color: 'var(--accent)' },
    completed:  { background: 'var(--color-success-dim)', border: '1px solid var(--color-success-border)', color: 'var(--color-success)' },
    cancelled:  { background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)', color: 'var(--color-danger)' },
  };

  const slotCardStyle = {
    open:      { background: 'var(--bg-input)', border: '1px dashed var(--border-accent)' },
    booked:    { background: 'var(--color-info-dim)', border: '1px solid var(--color-info-border)' },
    completed: { background: 'var(--color-success-dim)', border: '1px solid var(--color-success-border)' },
  };

  const ghostBtnStyle = {
    background: 'var(--accent-dim)',
    border: '1px solid var(--border-subtle)',
    color: 'var(--text-muted)',
  };

  return (
    <div className="space-y-8">
      {bookingSlot && (
        <BookSlotModal
          meetingDate={meeting?.meeting_date}
          onConfirm={handleBookingConfirm}
          onClose={() => setBookingSlot(null)}
        />
      )}

      {confirmRemove && (
        <ConfirmDialog
          message={confirmRemove.speechId
            ? 'Remove this speech? All comments and feedback will be permanently deleted.'
            : 'Unassign this speaker from the slot?'}
          confirmLabel="Remove"
          variant="danger"
          onConfirm={handleConfirmRemove}
          onCancel={() => setConfirmRemove(null)}
        />
      )}

      {/* Back link */}
      <Link
        to="/calendar"
        className="inline-block text-sm transition-colors"
        style={{ color: 'var(--text-faint)' }}
        onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)'; }}
      >
        ← Back to Schedule
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>
            {title || 'Weekly Meeting'}
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-faint)' }}>
            {formatDate(meeting_date)}
          </p>
        </div>
        <span
          className="px-3 py-1 rounded-full text-xs font-medium capitalize"
          style={statusStyle[status] || statusStyle.scheduled}
        >
          {status}
        </span>
      </div>

      {actionError && (
        <div
          className="px-4 py-3 rounded-xl text-sm font-medium"
          style={{
            background: 'var(--color-danger-dim)',
            border: '1px solid var(--color-danger-border)',
            color: 'var(--color-danger)',
          }}
        >
          ⚠ {actionError}
        </div>
      )}

      {/* Slots */}
      <section>
        <div className="flex items-center gap-2.5 mb-4">
          <div
            className="w-0.5 h-3 rounded-full flex-shrink-0"
            style={{ background: 'linear-gradient(180deg, var(--accent) 0%, transparent 100%)' }}
          />
          <h2 className="font-mono text-xs font-medium uppercase tracking-[0.2em]" style={{ color: 'var(--text-faint)' }}>
            Speaking Slots
            <span className="ml-2 normal-case tracking-normal font-mono" style={{ color: 'var(--text-faint)', opacity: 0.6 }}>
              {bookedCount}/{slots.length} filled
            </span>
          </h2>
        </div>

        <div className="space-y-3">
          {slots.map((slot) => {
            const isOwn = slot.speaker_id === member?.member_id;
            const speechForSlot = speeches.find(sp => sp.slot_id === slot.slot_id);
            const isCompletedMeeting = status === 'completed';
            const canBook = !isPast && !memberSlot && slot.status === 'open' && status !== 'cancelled';
            const canUpload = isAdmin && isTodayOrPast && !speechForSlot &&
              (slot.status === 'booked' || slot.status === 'open');
            const isAssignOpen = assignSlotId === slot.slot_id;
            const isUploadOpen = uploadSlotId === slot.slot_id;
            const cardStyle = {
              ...slotCardStyle[slot.status] || slotCardStyle.open,
              borderRadius: (isAssignOpen || isUploadOpen) ? '0.75rem 0.75rem 0 0' : '0.75rem',
              borderBottom: (isAssignOpen || isUploadOpen) ? 'none' : undefined,
            };

            return (
              <div key={slot.slot_id}>
                {/* Slot card */}
                <div style={cardStyle} className="p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    {/* Left: slot number + info */}
                    <div className="flex items-start gap-3 min-w-0">
                      <span
                        className="font-mono text-xs mt-0.5 flex-shrink-0"
                        style={{ color: 'var(--text-faint)' }}
                      >
                        #{slot.slot_number}
                      </span>
                      <div className="min-w-0">
                        {slot.status === 'open' ? (
                          <span className="text-sm italic" style={{ color: 'var(--text-faint)' }}>
                            Open slot
                          </span>
                        ) : (
                          <>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                {slot.speaker_name || 'Speaker'}
                              </span>
                              {isOwn && (
                                <span className="text-xs font-medium" style={{ color: 'var(--accent)' }}>
                                  (You)
                                </span>
                              )}
                              {slot.status === 'completed' && (
                                <span className="text-xs font-medium" style={{ color: 'var(--color-success)' }}>
                                  ✓ Completed
                                </span>
                              )}
                            </div>
                            {slot.presentation_title && (
                              <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                                "{slot.presentation_title}"
                              </p>
                            )}
                            {slot.synopsis && (
                              <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                                {slot.synopsis}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Right: action buttons */}
                    <div className="flex items-center gap-2 flex-wrap sm:shrink-0 sm:flex-nowrap">
                      {/* Admin: delete open slot */}
                      {isAdmin && slot.status === 'open' && (
                        <button
                          onClick={() => handleDeleteSlot(slot.slot_id)}
                          className="text-sm px-3 py-2 rounded-lg transition-colors"
                          style={{ ...ghostBtnStyle, color: 'var(--color-danger)', opacity: 0.7 }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; }}
                        >
                          Delete slot
                        </button>
                      )}

                      {/* Non-admin: book slot */}
                      {!isAdmin && canBook && (
                        <button
                          onClick={() => handleBookSlot(slot)}
                          className="btn-bouncy text-sm px-4 py-2"
                        >
                          Book Slot
                        </button>
                      )}
                      {/* Non-admin: cancel own booking */}
                      {!isAdmin && canCancel && isOwn && slot.status === 'booked' && !isPast && (
                        <button
                          onClick={() => handleCancelSlot(slot.slot_id)}
                          className="text-sm px-3 py-2 rounded-lg transition-colors"
                          style={ghostBtnStyle}
                        >
                          Cancel
                        </button>
                      )}

                      {/* Admin: assign/edit/unassign (upcoming or completed meetings) */}
                      {isAdmin && slot.status !== 'completed' && status !== 'cancelled' &&
                        (!isPast || isCompletedMeeting) && (
                        <>
                          {!isAssignOpen && (
                            <button
                              onClick={() => openAssignForm(slot)}
                              className="btn-bouncy text-sm px-3 py-2"
                            >
                              {slot.status === 'open' ? 'Assign Speaker' : 'Edit'}
                            </button>
                          )}
                          {slot.status === 'booked' && !isAssignOpen && (
                            <button
                              onClick={() => handleUnassignSlot(slot.slot_id)}
                              className="text-sm px-3 py-2 rounded-lg transition-colors"
                              style={ghostBtnStyle}
                            >
                              Unassign
                            </button>
                          )}
                          {isAssignOpen && (
                            <button
                              onClick={() => setAssignSlotId(null)}
                              className="text-sm"
                              style={{ color: 'var(--text-faint)' }}
                            >
                              Cancel
                            </button>
                          )}
                        </>
                      )}

                      {/* Admin: force-remove speaker from completed slot to reassign */}
                      {isAdmin && slot.status === 'completed' && status !== 'cancelled' && (
                        <button
                          onClick={() => handleRemoveClick(slot.slot_id, speechForSlot?.speech_id || null)}
                          className="text-sm px-3 py-2 rounded-lg transition-colors"
                          style={{ ...ghostBtnStyle, color: 'var(--color-danger)' }}
                          onMouseEnter={e => { e.currentTarget.style.color = 'var(--color-danger)'; }}
                          onMouseLeave={e => { e.currentTarget.style.color = 'var(--color-danger)'; }}
                        >
                          Remove
                        </button>
                      )}

                      {/* Admin: add YouTube recording (past/completed, no speech yet) */}
                      {canUpload && !isUploadOpen && (
                        <button
                          onClick={() => openUploadForm(slot)}
                          className="btn-bouncy text-sm px-3 py-2"
                        >
                          {isPast ? 'Add Recording' : 'Log Speech'}
                        </button>
                      )}
                      {isUploadOpen && (
                        <button
                          onClick={() => setUploadSlotId(null)}
                          className="text-sm"
                          style={{ color: 'var(--text-faint)' }}
                        >
                          Cancel
                        </button>
                      )}

                      {/* View/feedback link — show whenever a speech record exists */}
                      {speechForSlot && (
                        <Link
                          to={`/speeches/${speechForSlot.speech_id}`}
                          className="text-sm px-3 py-2 rounded-lg transition-colors"
                          style={{
                            background: 'var(--color-success-dim)',
                            border: '1px solid var(--color-success-border)',
                            color: 'var(--color-success)',
                          }}
                        >
                          {slot.status === 'completed' ? 'View Speech' : 'Give Feedback →'}
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Per-slot background-transcription banner — survives refresh because the
                      condition is DB-backed on the speech row. Requires transcript AND metrics
                      to both be missing: a row with a transcript but no metrics is not mid-flight
                      (transcription writes both together), so polling it would spin forever. */}
                  {speechForSlot?.audio_file_id && !speechForSlot?.metrics && !speechForSlot?.transcript && (
                    <div className="px-4 pb-3">
                      <SlotTranscriptionBanner
                        speechId={speechForSlot.speech_id}
                        durationSeconds={uploadDurations[slot.slot_id] || 0}
                        onComplete={(updatedSpeech) => {
                          setSpeeches(prev => prev.map(sp =>
                            sp.speech_id === updatedSpeech.speech_id ? { ...sp, ...updatedSpeech } : sp
                          ));
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Inline assign form (admin, upcoming) */}
                {isAssignOpen && (
                  <div
                    className="p-4 space-y-3"
                    style={{
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--color-info-border)',
                      borderTop: 'none',
                      borderRadius: '0 0 0.75rem 0.75rem',
                    }}
                  >
                    <p className="text-xs font-semibold font-mono uppercase tracking-wider" style={{ color: 'var(--accent)' }}>
                      Assign speaker — Slot {slot.slot_number}
                    </p>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                        Speaker <span style={{ color: 'var(--accent)' }}>*</span>
                      </label>
                      <MemberPicker
                        members={allMembers}
                        value={assignForm.speaker_id}
                        onChange={(id) => setAssignForm(f => ({ ...f, speaker_id: id }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                        Presentation Title
                      </label>
                      <input
                        type="text"
                        value={assignForm.presentation_title}
                        onChange={(e) => setAssignForm(f => ({ ...f, presentation_title: e.target.value }))}
                        placeholder="e.g. The Power of Storytelling"
                        style={inputStyle}
                        onFocus={focusAmber}
                        onBlur={blurAmber}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                        Synopsis <span className="font-normal" style={{ color: 'var(--text-faint)' }}>(visible to all members)</span>
                      </label>
                      <textarea
                        value={assignForm.synopsis}
                        onChange={(e) => setAssignForm(f => ({ ...f, synopsis: e.target.value }))}
                        placeholder="Brief description of what the speech will cover…"
                        rows={2}
                        style={{ ...inputStyle, resize: 'none' }}
                        onFocus={focusAmber}
                        onBlur={blurAmber}
                      />
                    </div>
                    {assignMessage && (
                      <p className="text-sm" style={{ color: assignMessage.type === 'error' ? 'var(--color-danger)' : 'var(--color-success)' }}>
                        {assignMessage.text}
                      </p>
                    )}
                    <button
                      onClick={() => handleAssignSlot(slot)}
                      disabled={assigning}
                      className="btn-bouncy text-sm px-4 py-2 disabled:opacity-50"
                    >
                      {assigning ? 'Assigning…' : 'Assign'}
                    </button>
                  </div>
                )}

                {/* Inline YouTube upload form (admin, past meeting) */}
                {isUploadOpen && (
                  <div
                    className="p-4 space-y-3"
                    style={{
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-accent)',
                      borderTop: 'none',
                      borderRadius: '0 0 0.75rem 0.75rem',
                    }}
                  >
                    <p className="text-xs font-semibold font-mono uppercase tracking-wider" style={{ color: 'var(--accent)' }}>
                      Add recording — {slot.speaker_name || (slot.status === 'open' ? 'assign speaker below' : 'Speaker')}
                    </p>
                    {slot.status === 'open' && (
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                          Speaker <span style={{ color: 'var(--accent)' }}>*</span>
                        </label>
                        <MemberPicker
                          members={allMembers}
                          value={uploadForm.speakerId}
                          onChange={(id) => setUploadForm(f => ({ ...f, speakerId: id }))}
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                        Speech Title <span style={{ color: 'var(--accent)' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={uploadForm.title}
                        onChange={(e) => setUploadForm(f => ({ ...f, title: e.target.value }))}
                        placeholder="e.g. The Power of Storytelling"
                        style={inputStyle}
                        onFocus={focusAmber}
                        onBlur={blurAmber}
                      />
                    </div>
                    {/* Recording source — pick one */}
                    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-subtle)' }}>
                      {/* Option A: YouTube */}
                      <div className="p-3 space-y-2" style={{ background: uploadForm.youtubeUrl ? 'var(--color-info-dim)' : 'var(--bg-input)' }}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: 'var(--accent-dim)', color: 'var(--accent)' }}>A</span>
                          <label className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                            Paste a YouTube link
                          </label>
                          <span className="text-[10px]" style={{ color: 'var(--text-faint)' }}>— adds video to the library</span>
                        </div>
                        <input
                          type="url"
                          value={uploadForm.youtubeUrl}
                          onChange={(e) => setUploadForm(f => ({ ...f, youtubeUrl: e.target.value }))}
                          placeholder="https://youtube.com/watch?v=… or youtu.be/…"
                          style={inputStyle}
                          onFocus={focusAmber}
                          onBlur={blurAmber}
                        />
                      </div>
                      {/* Divider */}
                      <div className="flex items-center gap-2 px-3 py-1.5" style={{ borderTop: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-canvas)' }}>
                        <div className="flex-1 h-px" style={{ background: 'var(--border-subtle)' }} />
                        <span className="text-[10px] font-mono font-bold uppercase tracking-wider" style={{ color: 'var(--text-faint)' }}>or</span>
                        <div className="flex-1 h-px" style={{ background: 'var(--border-subtle)' }} />
                      </div>
                      {/* Option B: File upload */}
                      <div className="p-3 space-y-2" style={{ background: uploadForm.audioFile ? 'var(--color-success-dim)' : 'var(--bg-input)' }}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: 'var(--color-success-dim)', color: 'var(--color-success)' }}>B</span>
                          <label className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                            Upload an audio or video file
                          </label>
                          <span className="text-[10px]" style={{ color: 'var(--text-faint)' }}>— triggers AI analysis</span>
                        </div>
                        <input
                          type="file"
                          accept="audio/*,video/*"
                          onChange={(e) => setUploadForm(f => ({ ...f, audioFile: e.target.files?.[0] || null }))}
                          className="block w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-[var(--accent-dim)] file:text-[color:var(--accent)] hover:file:opacity-80 cursor-pointer"
                          style={{ color: 'var(--text-muted)' }}
                        />
                        {uploadForm.audioFile && (
                          <p className="text-xs" style={{ color: 'var(--text-faint)' }}>
                            {uploadForm.audioFile.name} ({(uploadForm.audioFile.size / 1024 / 1024).toFixed(1)} MB)
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                        Tags <span className="font-normal" style={{ color: 'var(--text-faint)' }}>(comma-separated, optional)</span>
                      </label>
                      <input
                        type="text"
                        value={uploadForm.tagsInput}
                        onChange={(e) => setUploadForm(f => ({ ...f, tagsInput: e.target.value }))}
                        placeholder="Leadership, Storytelling, Technical"
                        style={inputStyle}
                        onFocus={focusAmber}
                        onBlur={blurAmber}
                      />
                    </div>
                    {uploadMessage && (
                      <p className="text-sm" style={{ color: uploadMessage.type === 'error' ? 'var(--color-danger)' : 'var(--color-success)' }}>
                        {uploadMessage.text}
                      </p>
                    )}
                    <button
                      onClick={() => handleUploadSpeech(slot)}
                      disabled={uploading}
                      className="btn-bouncy text-sm px-4 py-2 disabled:opacity-50"
                    >
                      {uploading && uploadForm.audioFile
                        ? `Uploading… ${uploadPercent}%`
                        : uploading ? 'Saving…' : 'Save Recording'}
                    </button>
                    {uploading && uploadForm.audioFile && (
                      <div
                        className="w-full rounded-full overflow-hidden"
                        style={{ height: '4px', background: 'var(--bg-input)' }}
                      >
                        <div
                          className="h-full transition-all duration-300"
                          style={{
                            width: `${uploadPercent}%`,
                            borderRadius: '9999px',
                            background: 'linear-gradient(90deg, var(--accent), var(--color-info))',
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Admin: add a slot beyond the default count */}
        {isAdmin && status !== 'cancelled' && (
          <button
            onClick={handleAddSlot}
            className="mt-3 text-sm font-medium transition-colors"
            style={{ color: 'var(--text-faint)' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)'; }}
          >
            + Add slot
          </button>
        )}
      </section>

      {/* Speeches */}
      {speeches.length > 0 && (
        <section>
          <div className="flex items-center gap-2.5 mb-4">
            <div
              className="w-0.5 h-3 rounded-full flex-shrink-0"
              style={{ background: 'linear-gradient(180deg, var(--accent) 0%, transparent 100%)' }}
            />
            <h2 className="font-mono text-xs font-medium uppercase tracking-[0.2em]" style={{ color: 'var(--text-faint)' }}>
              Speeches
            </h2>
          </div>
          <div className="space-y-2">
            {speeches.map((speech) => (
              <Link
                key={speech.speech_id}
                to={`/speeches/${speech.speech_id}`}
                className="block p-4 rounded-xl transition-colors"
                style={{
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-subtle)',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-accent)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
              >
                <p className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{speech.title}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-faint)' }}>
                  by {speech.speaker_name || 'Speaker'}
                </p>
                {speech.metrics && (
                  <div className="flex items-center gap-4 mt-2">
                    {speech.metrics.wpm !== undefined && (
                      <span className="font-mono text-xs" style={{ color: 'var(--text-faint)' }}>
                        {Math.round(speech.metrics.wpm)} WPM
                      </span>
                    )}
                    {speech.metrics.confidence_score !== undefined && (
                      <span className="font-mono text-xs" style={{ color: 'var(--text-faint)' }}>
                        {Math.round(speech.metrics.confidence_score)}% fluency
                      </span>
                    )}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {speeches.length === 0 && isPast && (
        <div
          className="rounded-xl p-6 text-center"
          style={{ background: 'var(--accent-dim)', border: '1px dashed var(--border-accent)' }}
        >
          <p className="text-sm" style={{ color: 'var(--text-faint)' }}>
            No speeches recorded for this meeting.
          </p>
        </div>
      )}

      {/* Attendance */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div
              className="w-0.5 h-3 rounded-full flex-shrink-0"
              style={{ background: 'linear-gradient(180deg, var(--accent) 0%, transparent 100%)' }}
            />
            <h2 className="font-mono text-xs font-medium uppercase tracking-[0.2em]" style={{ color: 'var(--text-faint)' }}>
              Attendance
              {attendance.length > 0 && (
                <span className="ml-2 normal-case tracking-normal font-mono" style={{ color: 'var(--text-faint)', opacity: 0.6 }}>
                  {attendance.length} checked in
                </span>
              )}
            </h2>
          </div>
          {isAdmin && status !== 'cancelled' && (
            <div className="flex gap-2">
              <button
                onClick={handleGenerateQR}
                disabled={qrLoading}
                className="btn-bouncy text-sm px-3 py-2 disabled:opacity-50"
              >
                {qrLoading ? 'Loading…' : (() => {
                  const exp = meeting?.checkin_expires_at ? Number(meeting.checkin_expires_at) : 0;
                  return exp > Date.now() ? 'View QR' : 'Generate QR';
                })()}
              </button>
              <button
                onClick={handleShowMarkPresent}
                className="text-sm px-3 py-2 rounded-lg transition-colors"
                style={ghostBtnStyle}
              >
                Mark Present
              </button>
            </div>
          )}
        </div>

        {attendance.length === 0 ? (
          <div
            className="rounded-xl p-6 text-center"
            style={{ background: 'var(--accent-dim)', border: '1px dashed var(--border-accent)' }}
          >
            <p className="text-sm" style={{ color: 'var(--text-faint)' }}>No one has checked in yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {attendance.map((a) => (
              <div
                key={a.attendance_id}
                className="flex items-center justify-between px-4 py-2.5 rounded-lg"
                style={{
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-subtle)',
                }}
              >
                <div className="flex items-center gap-2">
                  <span style={{ color: 'var(--color-success)', fontSize: '0.8rem' }}>✓</span>
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{a.member_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className="px-2 py-0.5 rounded-full text-xs"
                    style={
                      a.method === 'qr'
                        ? { background: 'var(--color-info-dim)', border: '1px solid var(--color-info-border)', color: 'var(--color-info)' }
                        : { background: 'var(--accent-dim)', border: '1px solid var(--border-subtle)', color: 'var(--text-faint)' }
                    }
                  >
                    {a.method === 'qr' ? 'QR' : 'Manual'}
                  </span>
                  <span className="font-mono text-xs" style={{ color: 'var(--text-faint)' }}>
                    {a.checked_in_at && (() => {
                      const d = new Date(Number(a.checked_in_at));
                      const isToday = d.toDateString() === new Date().toDateString();
                      const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
                      const date = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                      return isToday ? `Today, ${time}` : `${date}, ${time}`;
                    })()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {showMarkPresent && isAdmin && (() => {
          const notCheckedIn = allMembers.filter(m => !attendance.find(a => a.member_id === m.member_id));
          const q = markPresentSearch.toLowerCase().trim();
          const filtered = q
            ? notCheckedIn.filter(m => `${m.first_name} ${m.last_name}`.toLowerCase().includes(q))
            : notCheckedIn;
          return (
            <div
              className="mt-4 rounded-xl overflow-hidden"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-accent)' }}
            >
              {/* Header + search row */}
              <div
                className="flex items-center gap-3 px-4 py-3"
                style={{ borderBottom: '1px solid var(--border-subtle)' }}
              >
                <input
                  type="text"
                  placeholder="Search members…"
                  autoFocus
                  value={markPresentSearch}
                  onChange={e => setMarkPresentSearch(e.target.value)}
                  className="flex-1 text-sm rounded-lg px-3 py-2 outline-none"
                  style={{
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border-subtle)',
                    color: 'var(--text-primary)',
                  }}
                />
                <span className="text-xs font-mono flex-shrink-0" style={{ color: 'var(--text-faint)' }}>
                  {filtered.length} / {notCheckedIn.length}
                </span>
                <button
                  onClick={() => { setShowMarkPresent(false); setMarkPresentSearch(''); }}
                  className="text-sm flex-shrink-0 transition-colors"
                  style={{ color: 'var(--text-faint)' }}
                  onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)'; }}
                >
                  Close
                </button>
              </div>

              {/* Member list */}
              <div className="divide-y" style={{ borderColor: 'var(--border-subtle)', maxHeight: '320px', overflowY: 'auto' }}>
                {filtered.map((m) => (
                  <div
                    key={m.member_id}
                    className="flex items-center justify-between px-4 py-3"
                  >
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {m.first_name} {m.last_name}
                    </span>
                    <button
                      onClick={() => handleMarkPresent(m.member_id)}
                      className="text-xs px-4 py-1.5 rounded-lg transition-colors flex-shrink-0"
                      style={{
                        background: 'var(--color-success-dim)',
                        border: '1px solid var(--color-success-border)',
                        color: 'var(--color-success)',
                      }}
                    >
                      Mark Present
                    </button>
                  </div>
                ))}
                {notCheckedIn.length === 0 && (
                  <p className="text-sm text-center py-4" style={{ color: 'var(--text-faint)' }}>
                    All members are already checked in.
                  </p>
                )}
                {notCheckedIn.length > 0 && filtered.length === 0 && (
                  <p className="text-sm text-center py-4" style={{ color: 'var(--text-faint)' }}>
                    No members match "{markPresentSearch}".
                  </p>
                )}
              </div>
            </div>
          );
        })()}
      </section>

      {/* QR Code Modal */}
      {qrModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setQrModal(null); }}
        >
          <div
            className="w-full max-w-md rounded-2xl p-6"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-accent)' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                Meeting Check-in QR
              </h3>
              <button
                onClick={() => setQrModal(null)}
                className="text-xl transition-colors"
                style={{ color: 'var(--text-faint)' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)'; }}
              >
                ×
              </button>
            </div>
            <div className="text-center">
              <img
                src={qrModal.qrDataUrl}
                alt="Check-in QR Code"
                className="mx-auto mb-4 rounded-xl"
                style={{ width: 280, height: 280 }}
              />
              <p className="text-sm mb-2" style={{ color: 'var(--text-muted)' }}>
                Scan with your phone camera to check in
              </p>
              {qrModal.expiresAt && (
                <p className="font-mono text-xs mb-4" style={{ color: 'var(--text-faint)' }}>
                  Expires: {new Date(Number(qrModal.expiresAt)).toLocaleTimeString('en-IN', {
                    hour: '2-digit', minute: '2-digit',
                  })}
                </p>
              )}
              <button
                onClick={() => navigator.clipboard.writeText(qrModal.checkinUrl)}
                className="px-4 py-2 text-sm rounded-lg transition-colors"
                style={ghostBtnStyle}
              >
                Copy Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
